// Helpers for pipe-delimited TTS voice strings, e.g.
// "voice_name=Puck|model=gemini-3.8-flash-tts|speed=1.05|prompt=Warm and upbeat"

const PROMPT_KEYS = ["prompt", "style"];

const getSegmentKey = (segment: string): string => {
    const separatorIndex = segment.indexOf("=");
    return separatorIndex < 0 ? "" : segment.substring(0, separatorIndex).trim().toLowerCase();
};

const isPromptSegment = (segment: string): boolean => PROMPT_KEYS.includes(getSegmentKey(segment));

const stripQuotes = (value: string): string => {
    if (value.length >= 2 &&
        ((value.startsWith("\"") && value.endsWith("\"")) || (value.startsWith("'") && value.endsWith("'")))) {
        return value.substring(1, value.length - 1);
    }
    return value;
};

/** Only Gemini voices read a style prompt from the voice string. */
export const supportsVoicePrompt = (ttsProvider?: string): boolean =>
    (ttsProvider || "").trim().toLowerCase() === "gemini";

export const getVoiceStringPrompt = (voice?: string): string => {
    const promptSegment = (voice || "").split("|").find(isPromptSegment);
    if (!promptSegment) return "";
    return stripQuotes(promptSegment.substring(promptSegment.indexOf("=") + 1).trim());
};

export const removeVoiceStringPrompt = (voice?: string): string =>
    (voice || "").split("|").filter(segment => !isPromptSegment(segment)).join("|");

/**
 * The voice string is pipe-delimited and the prompt is saved in double quotes,
 * so a prompt can't contain pipes, line breaks or double quotes.
 */
export const sanitizeVoicePrompt = (prompt?: string): string =>
    (prompt || "").replace(/[|\r\n]+/g, " ").replace(/"/g, "'");

export const setVoiceStringPrompt = (voice: string, prompt?: string): string => {
    let base = removeVoiceStringPrompt(voice);
    const cleanPrompt = sanitizeVoicePrompt(prompt);
    if (!cleanPrompt.trim()) return base;

    // A bare voice name stops being read as the name once other settings are added.
    if (base.trim() && !base.includes("=")) {
        base = `voice_name=${base.trim()}`;
    }

    // Quoted, because the API only keeps quoted prompts when a greeting switches Gemini voices.
    return base ? `${base}|prompt="${cleanPrompt}"` : `prompt="${cleanPrompt}"`;
};

/** Carries a prompt over to another voice string, leaving it untouched when there is no prompt. */
export const carryVoiceStringPrompt = (voice: string, prompt?: string): string =>
    (prompt || "").trim() ? setVoiceStringPrompt(voice, prompt) : voice;

const normalizeVoiceString = (voice?: string): string =>
    removeVoiceStringPrompt(voice).split("|").map(segment => segment.trim()).filter(Boolean).join("|");

/** Compares two voice strings while ignoring any style prompt. */
export const voiceStringsMatch = (left?: string, right?: string): boolean => {
    const normalizedLeft = normalizeVoiceString(left);
    return !!normalizedLeft && normalizedLeft === normalizeVoiceString(right);
};

/** Compares two voice strings including their style prompt. */
export const voiceStringsMatchExactly = (left?: string, right?: string): boolean =>
    voiceStringsMatch(left, right) && getVoiceStringPrompt(left) === getVoiceStringPrompt(right);

/** Whether a voice string comes with its own style prompt (e.g. the Jori presets). */
export const hasVoiceStringPrompt = (voice?: string): boolean => !!getVoiceStringPrompt(voice).trim();
